import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { FileText, CheckCircle, Clock, Filter, Search, MessageCircle, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeaveHistory({ user, onLogout }) {
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    approvalRate: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaves();
  }, []);

  useEffect(() => {
    applySearch();
  }, [searchTerm, leaves]);

  const fetchLeaves = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leaves`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const leavesData = response.data;
      setLeaves(leavesData);
      
      // Calculate stats
      const total = leavesData.length;
      const approved = leavesData.filter(l => l.status === 'Approved').length;
      const pending = leavesData.filter(l => l.status === 'Pending').length;
      const approvalRate = total > 0 ? ((approved / total) * 100).toFixed(0) : 0;
      
      setStats({ total, approved, pending, approvalRate });
    } catch (error) {
      toast.error('Failed to fetch leave history');
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    if (!searchTerm.trim()) {
      setFilteredLeaves(leaves);
      return;
    }
    
    const filtered = leaves.filter(l => 
      l.leave_reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.leave_type.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredLeaves(filtered);
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'Approved') return 'bg-green-100 text-green-800 border-green-200';
    if (status === 'Pending') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const displayedLeaves = filteredLeaves.slice(0, rowsPerPage);

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 md:ml-0">
        {/* Header */}
        <div className="bg-white border-b border-border px-4 md:px-8 py-4 md:py-6 pt-16 md:pt-6">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Personal Leave History</h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1">Archive of all your leave requests</p>
        </div>

        <div className="p-4 md:p-8">
          {/* Stat Cards */}
          <div className="grid grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm" data-testid="stat-total">
              <div className="flex items-start justify-between mb-2 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Total</p>
              <h3 className="text-2xl md:text-4xl font-bold font-heading text-foreground">{stats.total}</h3>
              <p className="text-xs text-muted-foreground mt-1 hidden md:block">All time</p>
            </div>

            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm" data-testid="stat-approved">
              <div className="flex items-start justify-between mb-2 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Approved</p>
              <h3 className="text-2xl md:text-4xl font-bold font-heading text-foreground">{stats.approved}</h3>
              <p className="text-xs text-muted-foreground mt-1 hidden md:block">{stats.approvalRate}% rate</p>
            </div>

            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm" data-testid="stat-pending">
              <div className="flex items-start justify-between mb-2 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-yellow-600" />
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Pending</p>
              <h3 className="text-2xl md:text-4xl font-bold font-heading text-foreground">{stats.pending}</h3>
              <p className="text-xs text-muted-foreground mt-1 hidden md:block">Awaiting</p>
            </div>
          </div>

          {/* Search and Filters - Hidden on mobile */}
          <div className="hidden md:block bg-white rounded-xl border border-border p-6 mb-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search leave reasons, comments..."
                  data-testid="search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <button className="flex items-center gap-2 px-4 py-3 border border-input rounded-lg bg-white text-sm font-medium text-foreground hover:bg-muted transition-colors">
                <Filter className="w-4 h-4" />
                Filters
              </button>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3">
            {displayedLeaves.length === 0 ? (
              <div className="bg-white rounded-xl border border-border p-8 text-center text-muted-foreground">
                No leave requests found
              </div>
            ) : (
              displayedLeaves.map((leave) => (
                <div
                  key={leave.leave_id}
                  className="bg-white rounded-xl border border-border p-4 shadow-sm"
                  data-testid={`leave-card-${leave.leave_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="status-badge border text-xs bg-blue-100 text-blue-800 border-blue-200">
                        {leave.leave_type}
                      </span>
                      {/* Info button with notification dot */}
                      <button
                        onClick={() => setSelectedLeave(leave)}
                        className="relative p-1.5 hover:bg-muted rounded-lg"
                        title="View details"
                      >
                        <MessageCircle className="w-4 h-4 text-muted-foreground" />
                        <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${
                          leave.half_day_exceed_warning ? 'bg-red-500' : 'bg-green-500'
                        }`} />
                      </button>
                    </div>
                    <span className={`status-badge border text-xs ${getStatusBadgeClass(leave.status)}`}>
                      {leave.status}
                    </span>
                  </div>
                  
                  <p className="text-sm font-medium text-foreground mb-2">
                    {new Date(leave.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(leave.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                  
                  <div className="grid grid-cols-3 gap-2 text-center bg-muted/50 rounded-lg p-2">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Duration</p>
                      <p className="text-sm font-semibold text-foreground">{leave.days_off}d</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Paid</p>
                      <p className="text-sm font-semibold text-foreground">{leave.paid_leaves_count}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Unpaid</p>
                      <p className="text-sm font-semibold text-foreground">{leave.unpaid_leaves_count}</p>
                    </div>
                  </div>
                  
                  {leave.leave_reason && (
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                      {leave.leave_reason}
                    </p>
                  )}
                </div>
              ))
            )}
            
            {filteredLeaves.length > rowsPerPage && (
              <button
                onClick={() => setRowsPerPage(rowsPerPage + 10)}
                className="w-full py-3 text-sm text-primary font-medium hover:underline"
              >
                Load more...
              </button>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-xl border border-border shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <p className="text-sm text-muted-foreground">
                Showing {displayedLeaves.length > 0 ? 1 : 0} to {displayedLeaves.length} of {filteredLeaves.length} records
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="px-3 py-1 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leave-history-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Dates</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Leave Type</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Duration</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Info</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-border">
                  {displayedLeaves.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-muted-foreground">
                        No leave requests found
                      </td>
                    </tr>
                  ) : (
                    displayedLeaves.map((leave) => (
                      <tr
                        key={leave.leave_id}
                        className="hover:bg-muted/50 transition-colors"
                        data-testid={`leave-row-${leave.leave_id}`}
                      >
                        <td className="px-4 py-4 text-sm text-foreground">
                          <div>{new Date(leave.start_date).toLocaleDateString()}</div>
                          <div className="text-xs text-muted-foreground">to {new Date(leave.end_date).toLocaleDateString()}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1">
                            <span className="status-badge border bg-blue-100 text-blue-800 border-blue-200">
                              {leave.leave_type}
                            </span>
                            {leave.auto_converted_to_unpaid && (
                              <span className="text-xs text-orange-600" title="Auto-converted from paid leave">⚡</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center text-sm font-medium text-foreground">
                          {leave.days_off} {leave.days_off === 1 ? 'day' : 'days'}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => setSelectedLeave(leave)}
                            className="relative p-2 hover:bg-muted rounded-lg transition-colors mx-auto"
                            title="View details"
                          >
                            <MessageCircle className="w-5 h-5 text-muted-foreground" />
                            {/* Notification dot - red for exceed warning, green otherwise */}
                            <span className={`absolute top-1 right-1 w-2.5 h-2.5 rounded-full ${
                              leave.half_day_exceed_warning ? 'bg-red-500' : 'bg-green-500'
                            }`} />
                          </button>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className={`status-badge border ${getStatusBadgeClass(leave.status)}`} data-testid={`status-${leave.leave_id}`}>
                            {leave.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Leave Details Popup */}
      {selectedLeave && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedLeave(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-heading font-semibold">Leave Details</h2>
              </div>
              <button onClick={() => setSelectedLeave(null)} className="p-1 hover:bg-muted rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Warning Notification - Half Day Exceed */}
              {selectedLeave.half_day_exceed_warning && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">Half Day Limit Exceeded</span>
                  </div>
                  <p className="text-xs text-red-700 mt-1">
                    You have exceeded the half day threshold for this year. This leave request has been flagged for review.
                  </p>
                </div>
              )}

              {/* Auto Conversion Notice */}
              {selectedLeave.auto_converted_to_unpaid && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center gap-2 text-orange-800">
                    <span className="text-sm font-medium">⚡ Auto-converted to Unpaid</span>
                  </div>
                  <p className="text-xs text-orange-700 mt-1">
                    Your original request was for paid leave but was converted due to insufficient balance.
                  </p>
                </div>
              )}
              
              {/* Leave Reason */}
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-800 mb-1">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-medium">Leave Reason</span>
                </div>
                <p className="text-sm text-green-700">
                  {selectedLeave.leave_reason || 'No reason provided'}
                </p>
              </div>

              {/* Leave Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Leave Type:</span>
                  <p className="font-medium">{selectedLeave.leave_type}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Duration:</span>
                  <p className="font-medium">{selectedLeave.days_off} {selectedLeave.days_off === 1 ? 'day' : 'days'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Paid Leaves:</span>
                  <p className="font-medium">{selectedLeave.paid_leaves_count}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Unpaid Leaves:</span>
                  <p className="font-medium">{selectedLeave.unpaid_leaves_count}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <p className="font-medium">{selectedLeave.status}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Submitted:</span>
                  <p className="font-medium text-xs">{new Date(selectedLeave.created_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setSelectedLeave(null)}
              className="w-full mt-4 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/70 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
